import React, {useState} from "react"
import {Col} from "react-bootstrap"
import tracking from "@irati-tracking/wallet/rewardWallet"
import {CelebrationTag, User, PaymentCurrencyType, Wallet, WalletEntityType} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import History from "@irati/utils/history"
import {Body, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import {useWalletQuery} from "@irati/wallet/hooks/wallet"
import Flex from "@irati/ui/src/Flex"
import apps from "@irati-web/app/src/routeConfig"
import Tabs from "@irati/ui/src/Tabs"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {Row} from "@irati/ui/src/Layout"
import Reporting from "../../components/AdminReward/Reporting"
import ChristmasBanner from "../../components/campaigns/ChristmasBanner"
import Transactions from "../../components/AdminReward/Transactions"
import HeaderBalance from "../../components/AdminReward/Wallet/HeaderBalance"
import Intro from "../../components/AdminReward/Wallet/Intro"
import OnboardingSection from "../../components/OnboardingSection"
import RewardsOfferTopBanner from "../../components/RewardsOfferTopBanner"
import Container, {ContainerInline} from "../styled/Container"
import ExpiryModal from "../../components/AdminReward/Wallet/ExpiryModal"
import ViewRewards from "../../components/AdminReward/ViewRewards"
import AdminBudget from "../../components/AdminReward/Budget"
import TemplatesCarousel from "../../components/TemplatesCarousel"
import {WalletRoutes} from "../../enums/Routes"
import {DistributeRewardRecipient} from "../../enums/DistributeRewardTypes"
import {CTA} from "../../components/AdminReward/DistributeReward/styled"
import {ScheduledRewards} from "../../components/AdminReward/ScheduledRewards"
import CompanyBudgets from "../../components/AdminReward/CompanyBudgets"

function AdminRewardWalletWrapper() {
  const {user} = useAuth({})
  const {load, data: wallet} = useWalletQuery(
    {userId: user.uuid ?? "", currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward},
    {fetchPolicy: "network-only"}
  )
  return (
    <Loading load={load}>
      <RewardsOfferTopBanner />
      {wallet && <AdminRewardWallet user={user} wallet={wallet} />}
    </Loading>
  )
}

interface Props {
  user: User
  wallet: Wallet
}

function AdminRewardWallet({wallet, user}: Props) {
  const [showModal, setShowModal] = useState(false)
  const {canBudget, canViewCompanyBudgets} = useBudgetsPermissions()

  if (!canBudget) {
    return (
      <Container>
        <Row>
          <Col md={12} lg={5}>
            <Intro />
          </Col>
          <Col md={12} lg={7}>
            <HeaderBalance adminRewardWallet={wallet} onClick={() => setShowModal(!showModal)} />
          </Col>
        </Row>
        <CommonSections wallet={wallet} user={user} />
        <TransactionsSection wallet={wallet} user={user} />
        <ExpiryModal onClick={() => setShowModal(!showModal)} showModal={showModal} wallet={wallet} />
      </Container>
    )
  }

  if (!canViewCompanyBudgets) {
    return (
      <Container>
        <Intro />
        <AdminBudget wallet={wallet} onClick={() => setShowModal(!showModal)} />
        <CommonSections wallet={wallet} user={user} />
        <TransactionsSection wallet={wallet} user={user} />
        <ExpiryModal onClick={() => setShowModal(!showModal)} showModal={showModal} wallet={wallet} />
      </Container>
    )
  }

  return (
    <Container>
      <Intro />
      <Tabs
        id="headerBalances"
        mountOnEnter
        tabs={[
          {
            key: "Yours",
            title: <FormattedMessage id="wallet.reward.intro.tab.yours" />,
            component: (
              <ContainerInline>
                <AdminBudget wallet={wallet} onClick={() => setShowModal(!showModal)} />
                <CommonSections wallet={wallet} user={user} />
                <TransactionsSection wallet={wallet} user={user} />
              </ContainerInline>
            )
          },
          {
            key: "Everyones",
            title: <FormattedMessage id="wallet.reward.intro.tab.everyones" />,
            component: (
              <ContainerInline>
                <CompanyBudgets />
                <CommonSections wallet={wallet} user={user} />
              </ContainerInline>
            )
          }
        ]}
      />
      <ExpiryModal onClick={() => setShowModal(!showModal)} showModal={showModal} wallet={wallet} />
    </Container>
  )
}

function RewardTemplateSection() {
  const {formatMessage} = useIntl()

  const onTagSelected = (tag: CelebrationTag) => {
    tracking.rewardWalletClick(tag.name)
    const url = tag.default
      ? `${WalletRoutes.FLEXI_ASSIGN}/recipients`
      : `${WalletRoutes.FLEXI_ASSIGN}/recipients/?template=${tag.uuid}&recipient=${DistributeRewardRecipient.EMPLOYEE}`
    History.push(url, apps.WALLET)
  }

  return (
    <Flex direction="column">
      <H2>
        <FormattedMessage id="wallet.walletPage.templates.header" />
      </H2>
      <Flex gap={1} alignItems="center">
        <Body large maxWidth="unset">
          <FormattedMessage id="wallet.walletPage.templates.chooseTemplate.subtitle" />
        </Body>
        <CTA cta href={formatMessage({id: "wallet.reward.templates.learnMoreLink"})} target="_blank">
          <FormattedMessage id="wallet.reward.templates.learnMore" />
        </CTA>
      </Flex>
      <TemplatesCarousel onChange={onTagSelected} variant="flat" hideDefault />
    </Flex>
  )
}

function CommonSections({user}: Props) {
  return (
    <>
      <RewardTemplateSection />
      <ChristmasBanner />
      <ViewRewards />
      <OnboardingSection user={user} />
      <H2>
        <FormattedMessage id="wallet.flexiwallet.reports" />
      </H2>
      <Reporting />
    </>
  )
}

function TransactionsSection({wallet}: Props) {
  return (
    <div>
      <H2 marginBottom={2}>
        <FormattedMessage id="wallet.tenant.transactionsHeader" />
      </H2>
      <Tabs
        id="walletTransactions"
        mountOnEnter
        unmountOnExit
        tabs={[
          {
            key: "Completed",
            title: <FormattedMessage id="wallet.flexiwallet.transactions.heading.completed" />,
            component: <Transactions walletId={wallet.uuid} />
          },
          {
            key: "Scheduled",
            title: <FormattedMessage id="wallet.flexiwallet.transactions.heading.scheduled" />,
            component: <ScheduledRewards />
          }
        ]}
      />
    </div>
  )
}

export default AdminRewardWalletWrapper
